import type { Appointment } from "@myapp/contract";
import {
  CompleteAppointmentModal,
  EndAppointmentDepartureModal,
  FollowUpAppointmentModal,
} from "../../../features/work-session";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { WorkerAppointmentPlanOverlay, WorkerAppointmentReschedulePlanOverlay } from "../../../widgets/worker-appointment-plan";
import type { AppointmentFlows } from "../model/useAppointmentFlows";

type Props = {
  appointment: Appointment;
  technicianId: number;
  flows: AppointmentFlows;
};

/**
 * Every modal, overlay and success dialog the page can open, driven entirely by `flows`. Rendered
 * as a sibling of `PageLayout`, not inside it — `Modal` positions itself `fixed` rather than
 * through a portal, so nesting it inside a scrolling container would clip it.
 *
 * The three work-session modals stay mounted with a changing `show` (they reset via their own
 * `useResetOnShow` and participate in the shared modal stack) — only the two plan overlays are
 * conditionally mounted, since `CalendarPlanningOverlay` fetches before checking `show`.
 */
export const AppointmentFlowDialogs = ({ appointment, technicianId, flows }: Props) => {
  const { flow, close, onSessionSaved, onPlanned, onRescheduled, success } = flows;
  const setShow = (open: boolean) => { if (!open) close(); };

  return (
    <>
      <EndAppointmentDepartureModal
        show={flow === "end-departure"}
        setShow={setShow}
        appointmentId={appointment.id}
        workOrderId={appointment.workOrderId}
        actualEndsAt={appointment.schedule.actualEndsAt}
        onSuccess={() => onSessionSaved({ followUp: false })}
      />

      <CompleteAppointmentModal
        show={flow === "complete"}
        setShow={setShow}
        appointmentId={appointment.id}
        workOrderId={appointment.workOrderId}
        schedule={appointment.schedule}
        technicianComment={appointment.technicianComment}
        onSuccess={() => onSessionSaved({ followUp: false })}
      />

      <FollowUpAppointmentModal
        show={flow === "follow-up"}
        setShow={setShow}
        appointmentId={appointment.id}
        workOrderId={appointment.workOrderId}
        schedule={appointment.schedule}
        technicianComment={appointment.technicianComment}
        onSuccess={({ followUpAlreadyPlanned }) =>
          onSessionSaved({ followUp: true, followUpAlreadyPlanned })
        }
      />

      <SuccessDialog
        open={success.open}
        onOpenChange={(open) => { if (!open) success.onClose(); }}
        title={success.title}
        onClose={success.onClose}
        secondaryAction={success.secondaryAction}
      />

      {/* Mounted only while open — `CalendarPlanningOverlay` fetches the technician's schedule
          before its own `show` check, so an always-mounted overlay would fire that on every page
          load whether or not the worker ever opens it. */}
      {flow === "plan" && (
        <WorkerAppointmentPlanOverlay
          show
          onClose={close}
          onSuccess={onPlanned}
          workOrderId={appointment.workOrderId}
          technicianId={technicianId}
        />
      )}

      {flow === "reschedule" && (
        <WorkerAppointmentReschedulePlanOverlay
          show
          onClose={close}
          onSuccess={onRescheduled}
          appointment={appointment}
          technicianId={technicianId}
        />
      )}
    </>
  );
};
